import { computed, inject, Injectable, Injector, signal } from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ZonePageUrlProvider } from '@app/finxone-web-frontend/app/Modules/zones/zone-url-provider';
import { flattenObject, flattenObjectByLevel } from '@app/finxone-web-frontend/app/lib/utils/utils';
import { getNestedValue } from '@app/finxone-web-frontend/app/lib/utils/nested-object-path.utils';
import { FormID } from '@finxone-platform/form-action';
import { FormFieldConfig, UiZoneWidgetConfig } from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { SetFormActionWithId } from '../../actions/form-submission.action';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  of,
  Subscription,
  take,
} from 'rxjs';
import { FormActionState } from '../../state/form-submision.state';
import { ProjectSettingsState } from '../../state/project-settings.state';
import {
  getFormProgressDataAndSaveInState,
  submitFormInProgress,
} from '../../utils/form-widget-action-utils/form-tracking.utils';
import { minAgeValidator, phoneNumberValidator, phoneWithCodeValidator } from '../../utils/onboarding/utils';
import {
  createCustomValidators,
  createSyncCustomValidators,
} from '../../utils/validators/create-custom-validators';
import { formsAssociatedWithState } from '../../utils/workflow-forms-utils/workflow-forms.utils';
import { checkUrlParamsForValuesAndStoreInFormState, NestedFormDispatchMeta } from './workflow-forms.utils';

@Injectable({
  providedIn: 'root',
})
export class WorkflowFormsService {
  private readonly fb: FormBuilder = new FormBuilder();
  public forms = signal<Map<string, FormGroup>>(new Map());
  private formValidity = signal<Map<string, boolean>>(new Map());
  public formProgressDataAssigned = signal<boolean>(false);
  private formProgressSubscriptions = new Map<string, Subscription>();
  private formStateSubscriptions = new Map<string, Subscription>();
  private formDefaultValues = new Map<string, any>();

  // BehaviorSubject to track form changes
  private formsSubject = new BehaviorSubject<Map<string, FormGroup>>(this.forms());
  private readonly injector = inject(Injector);

  private setupFormValidityTracking(path: string, form: FormGroup) {
    form.valueChanges.subscribe(() => {
      const newMap = new Map(this.formValidity());
      newMap.set(path, this.isFormValid(path));
      this.formValidity.set(newMap);
    });

    // Initialize validity
    const newMap = new Map(this.formValidity());
    newMap.set(path, this.isFormValid(path));
    this.formValidity.set(newMap);
  }

  /**
   * Gets a nested form group based on a dot-notation path
   * @param path Path to the form group (e.g., 'onboarding.page1')
   * @returns FormGroup if found, undefined otherwise
   */
  getFormPart(path: string): FormGroup | undefined {
    const parts = path.split('.');
    let currentForm: FormGroup | undefined = undefined;

    // Handle root level form
    if (parts.length === 1) {
      return this.forms().get(parts[0]);
    }

    // Navigate through nested form groups
    currentForm = this.forms().get(parts[0]);
    if (!currentForm) return undefined;

    for (let i = 1; i < parts.length; i++) {
      currentForm = currentForm.get(parts[i]) as FormGroup;
      if (!currentForm) return undefined;
    }

    return currentForm;
  }

  /**
   * Creates a form group and any necessary parent groups based on the path
   * @param path Path where to create the form (e.g., 'onboarding.page1')
   * @param config Form configuration
   * @param registerForCurrentPage Whether to register `path` into currentPageForms (validity
   * tracking + on-submit dispatch). Pass false when this call is only building an intermediate
   * container FormGroup for a 'group'/'array' Form Widget (see resolveFormGroupTarget,
   * resolveArrayItemFormPath) - that path is never registered with nesting metadata, so if it
   * leaked into currentPageForms the Form Button would dispatch it flat onto a
   * "<rootFormID>.customForm-<pageUrl>" id instead of nesting under the real root form.
   */
  createForm(
    path: string,
    config: FormFieldConfig[],
    updateCurrentFormValue = false,
    registerForCurrentPage = true,
  ): void {
    const parts = path.split('.');
    const rootName = parts[0];

    // Create or get root form group
    let rootForm = this.forms().get(rootName);
    if (!rootForm) {
      rootForm = this.fb.group({});
      this.forms().set(rootName, rootForm);
    }

    // this needs to store each form, it requires when we validate the form
    this.setupFormValidityTracking(path, rootForm);

    // Build nested structure
    let currentForm = rootForm;
    for (let i = 1; i < parts.length; i++) {
      const part = parts[i];
      if (!currentForm.contains(part)) {
        currentForm.addControl(part, this.fb.group({}));
      }
      currentForm = currentForm.get(part) as FormGroup;
    }

    // Add form controls based on config
    this.addFormControl(currentForm, config, updateCurrentFormValue);
    // Store default values for this form before modifying it
    this.formDefaultValues.set(path, JSON.parse(JSON.stringify(currentForm.value)));
    //Assign values to the formGroup from a state if the form associated with any state when prevFormStateValue is not available
    this.checkFormTypeAndAssignValuesFromState(path);

    this.formsSubject.next(this.forms());
    // Register the form for current page validity tracking
    if (registerForCurrentPage) {
      this.registerCurrentPageForms([path]);
    }

    //Track form progress if its enable from project config
    this.setupFormProgressTracking(path);

    // this util method checks the url params and assign values to the form state under root name
    // this is needed to make sure the form pre-populates the data when provided from URL params
    // scenarios include external redirections to our app with query parameters for better UX
    checkUrlParamsForValuesAndStoreInFormState(rootName, this.injector);

    // Sync the form with state updates if applicable
    this.syncFormWithState(path);
  }

  /**
   * Sets up progress tracking for a specific form path.
   * Subscribes to value changes and triggers saving the form progress
   * if tracking is enabled for the form.
   */
  private setupFormProgressTracking(formPath: string): void {
    // Extract the root form ID from the form path
    const rootFormId = formPath.split('.')[0] as FormID;
    if (!this.isFormProgressEnabled(rootFormId)) return;

    const formGroup = this.getFormPart(formPath);
    if (!formGroup) return;

    // Initial fetch of form progress data to populate the form
    getFormProgressDataAndSaveInState(rootFormId, this.injector)
      .pipe(take(1))
      .subscribe({
        next: () => {
          // This flag is being used to reset the form field (dropdown) when selected value is no longer available
          // to select e.g; selected product not available anymore when user tries to resume their onboarding flow
          this.formProgressDataAssigned.set(true);
        },
      });

    if (!this.formProgressSubscriptions.get(formPath)) {
      const subscription = formGroup.valueChanges
        .pipe(debounceTime(5000), distinctUntilChanged())
        .subscribe(() => {
          submitFormInProgress(rootFormId, this.injector);
        });
      this.formProgressSubscriptions.set(formPath, subscription);
    }
  }

  /**
   * Syncs the form with state data only when form progress tracking is enabled
   * and a formSessionId is present in the URL.
   *
   * This method is mainly used to handle cases where:
   * - The form is created before API data is fetched
   * - Form progress data is loaded using formSessionId
   *
   * When the state updates with API data, the existing form
   * is patched with the latest values.
   *
   * Safeguards:
   * - Runs only if form progress is enabled for the form
   * - Runs only when formSessionId exists in the URL
   * - Prevents duplicate subscriptions for the same form
   * - Uses `emitEvent: false` to avoid triggering value change events
   *   and unnecessary API calls
   *
   * @param formPath Path of the form (e.g. 'onboarding.page1')
   */
  private syncFormWithState(formPath: string): void {
    const store = this.injector.get(Store);
    const activeRoute = this.injector.get(ActivatedRoute);
    const rootFormId = formPath.split('.')[0] as FormID;

    if (
      !this.isFormProgressEnabled(rootFormId) ||
      !activeRoute.snapshot?.queryParamMap?.get('formSessionId') ||
      this.formStateSubscriptions.has(formPath)
    )
      return;

    const subscription = store
      .select(FormActionState.getFormActionStateWithId(rootFormId))
      .pipe(distinctUntilChanged())
      .subscribe((state) => {
        const form = this.getFormPart(formPath);
        if (!state?.formData || !form) return;

        form.patchValue(state.formData, {
          emitEvent: false,
        });
      });

    this.formStateSubscriptions.set(formPath, subscription);
  }

  /**
   * Checks whether form progress tracking is enabled for a given form path.
   *
   * @param formPath The root form ID or path to check
   * @returns boolean indicating whether form progress tracking is enabled
   */
  private isFormProgressEnabled(formPath: FormID): boolean {
    const store = this.injector.get(Store);
    const projectConfig = store.selectSnapshot(ProjectSettingsState.getProjectSettings)?.projectConfig;

    return Boolean(projectConfig?.formProgress?.formId?.includes(formPath));
  }

  public addFormControl(currentForm: FormGroup, config: FormFieldConfig[], updateCurrentFormValue = false) {
    const newFormGroup = this.createFormGroupFromConfig(config);
    Object.keys(newFormGroup.controls).forEach((key) => {
      if (!currentForm.contains(key)) {
        currentForm.addControl(key, newFormGroup.get(key));
      } else {
        /**
         * Conditionally copies the value of a control from the current form into a new form group.
         *
         * If `updateCurrentFormValue` is `true`, and the current form has a value for the specified key,
         * the value is assigned to the corresponding control in the new form group. If the value is `null`
         * or `undefined`, an empty string is used instead.
         *
         * This is typically used when preparing a new form group while preserving values from an existing form.
         *
         */
        if (updateCurrentFormValue) {
          newFormGroup.get(key)?.setValue(currentForm.get(key)?.value ?? '');
        }
        currentForm.setControl(key, newFormGroup.get(key));
      }
    });
  }

  /**
   * Clears all forms from the service
   */
  clearAllForms(): void {
    this.forms().clear();
    this.formsSubject.next(this.forms());
    this.formProgressSubscriptions.forEach((sub) => sub.unsubscribe());
    this.formProgressSubscriptions.clear();
    this.formStateSubscriptions.forEach((sub) => sub.unsubscribe());
    this.formStateSubscriptions.clear();
  }

  /**
   * Notifies all observers that the forms map has changed.
   * Call this after manually registering a FormGroup into the tree
   * (e.g. via parentForm.addControl) so that observeForm() subscribers
   * receive the newly-registered form.
   */
  public notifyFormsChanged(): void {
    this.formsSubject.next(this.forms());
  }

  /**
   * Observes changes to a specific form path
   * @param path Path to the form to observe
   */
  observeForm(path: string): Observable<FormGroup | undefined> {
    return this.formsSubject.pipe(map(() => this.getFormPart(path)));
  }

  /**
   * Updates values in a specific form path
   * @param path Path to the form
   * @param values Values to patch
   */
  patchFormValues(path: string, values: any): void {
    const form = this.getFormPart(path);
    if (form) {
      form.patchValue(values, { emitEvent: true });
    }
  }

  /**
   * Validates a specific form path
   * @param path Path to the form to validate
   * @returns boolean indicating if the form is valid
   */
  isFormValid(path: string): boolean {
    const form = this.getFormPart(path);
    return form ? form.valid : false;
  }

  /**
   * Gets all validation errors for a specific form path
   * @param path Path to the form
   * @returns Object containing all validation errors
   */
  getFormErrors(path: string): { [key: string]: any } {
    const form = this.getFormPart(path);
    if (!form) return {};

    return this.getControlErrors(form);
  }

  /**
   * Resets a specific form to its initial state
   * @param path Path to the form to reset
   */
  resetForm(path: string): void {
    const form = this.getFormPart(path);
    if (form) {
      form.reset();
    }
  }

  /**
   * Starts a fresh run of a multi-page form: drops every page-scoped FormGroup under the root form
   * except the one at `keepPath`, then resets what is left. A plain reset would keep controls from
   * pages a previous run rendered - e.g. a required field this run hides via a hideCondition - which
   * then stay null and leave that page permanently invalid, since createForm never removes controls.
   * @param keepPath Path of the calling widget's own page form (e.g. 'onboarding.customForm-page1')
   */
  clearFormExceptPath(keepPath: string): void {
    const [rootName, keepKey] = keepPath.split('.');
    const rootForm = this.forms().get(rootName);
    if (!rootForm) return;

    Object.keys(rootForm.controls)
      .filter((key) => key !== keepKey)
      .forEach((key) => {
        rootForm.removeControl(key, { emitEvent: false });
        // The progress subscription is bound to the removed FormGroup; drop it so a recreated
        // form at the same path subscribes again (setupFormProgressTracking skips known paths).
        const removedPath = `${rootName}.${key}`;
        this.formProgressSubscriptions.forEach((subscription, path) => {
          if (path === removedPath || path.startsWith(`${removedPath}.`)) {
            subscription.unsubscribe();
            this.formProgressSubscriptions.delete(path);
          }
        });
      });

    rootForm.reset();
  }

  /**
   * Creates a form group from the provided configuration
   * @param config Form field configuration
   * @returns FormGroup
   */
  private createFormGroupFromConfig(config: FormFieldConfig[]): FormGroup {
    const group: any = {};

    config.forEach((field) => {
      if (field.type === 'group' && field.fields) {
        group[field.name] = this.createFormGroupFromConfig(field.fields);
      } else {
        group[field.name] = [
          field?.defaultValue ?? '',
          {
            validators: [...this.createValidators(field)],
            asyncValidators: this.createAsyncValidators(field),
            updateOn: 'change', // 'blur' | 'change'
          },
        ];
      }
    });

    return this.fb.group(group);
  }

  /**
   * Creates validators array from validation config
   * @param validation Validation configuration
   * @returns Array of validators
   */
  private createValidators(field: FormFieldConfig): ValidatorFn[] {
    const validators = [];

    if (field?.validation?.required) {
      validators.push(Validators.required);
    }
    if (field?.validation?.minLength) {
      validators.push(Validators.minLength(field?.validation.minLength));
    }
    if (field?.validation?.maxLength) {
      validators.push(Validators.maxLength(field?.validation.maxLength));
    }
    if (field?.validation?.pattern) {
      validators.push(Validators.pattern(field?.validation.pattern));
    }
    if (field?.validation?.requiredTrue) {
      validators.push(Validators.requiredTrue);
    }
    if (field?.validation?.email) {
      validators.push(Validators.email);
    }
    if (field?.validation?.phoneNumber) {
      if (field?.phoneNumberConfig?.attachToInput) {
        validators.push(phoneWithCodeValidator());
      } else {
        validators.push(phoneNumberValidator(field?.countryCode ?? 'countryCode'));
      }
    }
    if (field?.validation?.minAge) {
      validators.push(minAgeValidator(field.validation.minAge));
    }

    if (field.validation?.customValidator) {
      validators.push(field.validation.customValidator);
    }

    if (field.validation?.validators) {
      const customValidators = createSyncCustomValidators(field.validation.validators, this.injector);

      validators.push(...customValidators);
    }

    return validators;
  }

  private createAsyncValidators(field: FormFieldConfig): AsyncValidatorFn[] {
    const validators = [];

    if (field.validation?.validators) {
      const customValidators = createCustomValidators(field.validation.validators, this.injector);

      validators.push(...customValidators);
    }

    return [];
  }

  /**
   * Recursively gets all errors from a form control and its children
   * @param control Form control to check for errors
   * @returns Object containing all errors
   */
  private getControlErrors(control: AbstractControl): { [key: string]: any } {
    const errors: { [key: string]: any } = {};

    if (control instanceof FormGroup) {
      Object.keys(control.controls).forEach((key) => {
        const childErrors = this.getControlErrors(control.get(key)!);
        if (Object.keys(childErrors).length > 0) {
          errors[key] = childErrors;
        }
      });
    } else {
      if (control.errors) {
        return control.errors;
      }
    }

    return errors;
  }

  /**
   * Gets the complete value of a form at a specific path
   * @param path Path to the form
   * @returns Form values or undefined if form not found
   */
  getFormValues(path: string): any {
    const form = this.getFormPart(path);
    return form ? form.value : undefined;
  }

  getFormValuesFlattened(path: string): any {
    const form = this.getFormPart(path);

    const allValues: { [key: string]: any } = {};
    Object.assign(allValues, flattenObject(form?.value ?? {}));
    return allValues;
  }

  /**
   * Gets all form values combined into a parent form key with flattened object
   * @returns Object containing form value inside the parent form key
   */
  public getAllFormValuesFlattened(): Partial<{ [key in FormID]: any }> {
    const allValues: Partial<{ [key in FormID]: any }> = {};

    try {
      // Iterate over all forms registered in the service
      for (const [key, form] of this.forms().entries()) {
        const formValues = form.value;
        allValues[key as FormID] = flattenObjectByLevel(formValues, 1);
      }
      return allValues;
    } catch (error) {
      console.error('Error in getAllFormValuesFlattened:', error);
      // Return the partial data collected so far, or an empty object, to prevent crashing the caller
      return allValues;
    }
  }

  public observeAllFormsValuesFlattened(): Observable<Partial<{ [key in FormID]: any }>> {
    return of(this.getAllFormValuesFlattened());
  }

  /**
   * Marks all controls in a form as touched
   * @param path Path to the form
   */
  markFormTouched(path: string): void {
    const form = this.getFormPart(path);
    if (form) {
      Object.values(form.controls).forEach((control) => {
        control.markAsTouched();
        control.markAsDirty();
        control.updateValueAndValidity();
        if (control instanceof FormGroup) {
          this.markFormGroupTouched(control);
        }
      });
    }
  }

  /**
   * Recursively marks all controls in a form group as touched
   * @param formGroup Form group to mark as touched
   */
  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  // Track current page's form paths
  private currentPageForms = signal<string[]>([]);
  public currentPageFormsValid = computed(() => {
    return this.currentPageForms()
      .map((form) => {
        return this.formValidity().get(form);
      })
      .every((isValid) => isValid);
  });

  /**
   * Manually marks all current page forms as valid.
   *
   * This method updates the internal form validity map to set
   * the validity of all forms registered for the current page to true.
   * This can be useful when you want to bypass validation checks,
   * for example, when closing a modal or skipping validation temporarily.
   */
  public markAllCurrentPageFormsAsValid(): void {
    // Create a new Map cloning the current form validity state
    const updatedMap = new Map(this.formValidity());
    // Iterate over all form paths registered for the current page
    this.currentPageForms().forEach((formPath) => {
      // Set each form's validity to true manually
      updatedMap.set(formPath, true);
    });
    // Update the formValidity signal with the new map to trigger reactivity
    this.formValidity.set(updatedMap);
  }

  /**
   * Register forms that are currently active on the page
   * @param formPaths Array of form paths active on the current page
   */
  registerCurrentPageForms(formPaths: string[]): void {
    this.currentPageForms.set(formPaths);
  }

  // Maps a registered current-page form path to where its value nests in FormActionState.
  // Populated explicitly by callers that know their formType (FormWidgetComponent) so that
  // consumers like FormButtonWidgetComponent never have to re-derive nesting from the path
  // string's shape (e.g. guessing "ends in a digit" means an array index).
  private currentPageFormNestingMeta = new Map<string, NestedFormDispatchMeta>();

  /**
   * Register form widgets that are currently active on the page
   * @param formPath form name
   * @param nestedFormMeta Where this form's value nests in FormActionState - pass this whenever
   * the caller knows its formType (group/array/root), so dispatch-on-submit consumers can nest
   * correctly without inferring structure from formPath itself.
   */
  public registerCurrentPageFormWidgetForm(formPath: string, nestedFormMeta?: NestedFormDispatchMeta): void {
    const formPaths = this.currentPageForms();
    if (!formPaths.includes(formPath)) {
      formPaths.push(formPath);
    }
    // Update the current page forms signal
    this.currentPageForms.set(formPaths);
    if (nestedFormMeta) {
      this.currentPageFormNestingMeta.set(formPath, nestedFormMeta);
    }
  }

  /**
   * Gets the nesting metadata registered for a current-page form path, if any.
   * @param formPath form path as passed to registerCurrentPageFormWidgetForm
   */
  public getNestedFormDispatchMeta(formPath: string): NestedFormDispatchMeta | undefined {
    return this.currentPageFormNestingMeta.get(formPath);
  }

  /**
   * Clear current page forms when navigating away
   */
  clearCurrentPageForms(): void {
    this.currentPageForms.set([]);
    this.currentPageFormNestingMeta.clear();
  }

  /**
   * get current page forms
   * @returns Array of form paths for the current page
   */
  public getCurrentPageForms(): string[] {
    // Return the current page forms signal
    return this.currentPageForms();
  }

  /**
   * Check if all current page forms are valid
   * @returns Object containing validation status and invalid forms
   */
  validateCurrentPageForms(): {
    isValid: boolean;
    invalidForms: { path: string; errors: any }[];
  } {
    const invalidForms = this.currentPageForms()
      .filter((path) => !this.isFormValid(path))
      .map((path) => ({
        path,
        errors: this.getFormErrors(path),
      }));

    return {
      isValid: invalidForms.length === 0,
      invalidForms,
    };
  }

  /**
   * Mark all forms on the current page as touched
   */
  markCurrentPageFormsTouched(): void {
    this.currentPageForms().forEach((path) => {
      this.markFormTouched(path);
    });
  }

  /**
   * Adds validators to an existing control in a form at a given path
   * @param path Path to the form
   * @param controlName Name of the control
   * @param validators Array of validators to add
   */
  public addValidatorsToControl(path: string, controlName: string, validators: any[]): void {
    const form = this.getFormPart(path);
    if (form && form.contains(controlName)) {
      const control = form.get(controlName);
      if (control) {
        const existingValidators = control.validator ? [control.validator] : [];
        control.setValidators([...existingValidators, ...validators]);
        control.updateValueAndValidity();
      }
    }
  }

  /**
   * Removes all validators from an existing control in a form at a given path
   * @param path Path to the form
   * @param controlName Name of the control
   */
  public removeValidatorsFromControl(path: string, controlName: string): void {
    const form = this.getFormPart(path);
    if (form && form.contains(controlName)) {
      const control = form.get(controlName);
      if (control) {
        control.clearValidators();
        control.updateValueAndValidity();
      }
    }
  }

  // Create nested formGroup based on pageUrl
  public generateCustomFormId(path: string, zonePageUrlProvider?: ZonePageUrlProvider): string {
    const currentPageUrl = zonePageUrlProvider?.pageUrlInput;
    return `${path}.customForm-${currentPageUrl}`;
  }

  // Well-known FormActionState id used to remember which index of a given
  // FormArray (identified by "<rootFormID>.<childKey>") is currently being
  // added/edited, so that Form Widgets on different pages of a multi-page
  // wizard can all bind to the same array slot without any query-param plumbing.
  private static readonly ARRAY_EDIT_INDEX_STATE_ID = '__formArrayEditIndex';

  /**
   * Gets the index currently pinned for a given array path (e.g. "businessOnboardingFlow.directors").
   * Returns null when nothing is pinned (i.e. the next Form Widget to resolve this array should append).
   */
  public getActiveFormArrayIndex(arrayStateKey: string): number | null {
    const store = this.injector.get(Store);
    const state = store.selectSnapshot(
      FormActionState.getFormActionStateWithId(WorkflowFormsService.ARRAY_EDIT_INDEX_STATE_ID),
    );
    const value = state?.formData?.[arrayStateKey];
    return value === undefined || value === null ? null : Number(value);
  }

  /**
   * Pins the index being added/edited for a given array path. Pass null to mean
   * "append a new entry" - the next widget to resolve this array path will pin
   * the concrete index itself (current array length) via resolveArrayItemFormPath.
   */
  public setActiveFormArrayIndex(arrayStateKey: string, index: number | null): void {
    const store = this.injector.get(Store);
    store.dispatch(
      new SetFormActionWithId(
        { type: '', formData: { [arrayStateKey]: index } },
        WorkflowFormsService.ARRAY_EDIT_INDEX_STATE_ID,
      ),
    );
  }

  /**
   * Resolves (creating as needed) the FormGroup path for one entry of a FormArray
   * nested at `childKey` under `rootFormID`'s page-scoped form. childKey may be a
   * dot-path (e.g. "officers.items") - every segment but the last is walked/created
   * as a plain FormGroup, and the last segment is the FormArray itself. Reuses the
   * currently-pinned edit index for this array path if one exists (set via
   * setActiveFormArrayIndex, e.g. from an "Edit" action), otherwise allocates and pins
   * the next available index (i.e. "Add new").
   */
  public resolveArrayItemFormPath(
    rootFormID: string,
    childKey: string,
    zonePageUrlProvider?: ZonePageUrlProvider,
  ): { path: string; index: number } {
    const arrayStateKey = `${rootFormID}.${childKey}`;
    const parentFormPath = this.generateCustomFormId(rootFormID, zonePageUrlProvider);

    let parentForm = this.getFormPart(parentFormPath);
    if (!parentForm) {
      // registerForCurrentPage: false - see the matching comment in FormWidgetComponent's
      // 'group' branch; this is only the intermediate container, not a submittable path.
      this.createForm(parentFormPath, [], false, false);
      parentForm = this.getFormPart(parentFormPath) as FormGroup;
    }

    const segments = childKey.split('.');
    let currentGroup = parentForm;
    // Walk through all segments except the last, creating FormGroups as needed
    for (let i = 0; i < segments.length - 1; i++) {
      const segment = segments[i];
      if (!currentGroup.contains(segment)) {
        currentGroup.addControl(segment, new FormGroup({}));
      }
      currentGroup = currentGroup.get(segment) as FormGroup;
    }
    const arrayKey = segments[segments.length - 1];

    // Ensure the FormArray exists at the last segment, creating it if necessary
    if (!currentGroup.contains(arrayKey)) {
      currentGroup.addControl(arrayKey, new FormArray([]));
    }
    const arrayControl = currentGroup.get(arrayKey) as FormArray;

    // Determine the index to use for this array item, either from the pinned state or by allocating a new index
    let index = this.getActiveFormArrayIndex(arrayStateKey);
    if (index === null) {
      // Allocate the next slot based on the CURRENT persisted array length, not the live
      // FormArray's length - the live FormArray only ever grows (removeDirectorAt and friends
      // only ever touch NGXS state, never this array), so after a removal it ends up longer
      // than the real data and "Add new" would skip an index, leaving a null hole behind.
      const store = this.injector.get(Store);
      const persistedArray = getNestedValue(
        store.selectSnapshot(FormActionState.getFormActionStateWithId(rootFormID))?.formData,
        childKey,
      );
      index = Array.isArray(persistedArray) ? persistedArray.length : 0;
    }

    // Ensure the FormArray has enough slots to accommodate the desired index, creating empty FormGroups as needed
    while (arrayControl.length <= index) {
      arrayControl.push(new FormGroup({}));
    }

    this.setActiveFormArrayIndex(arrayStateKey, index);

    return { path: `${parentFormPath}.${childKey}.${index}`, index };
  }

  /**
   * Registers a FormGroup for current-page validity tracking without going through
   * the full createForm path-walk (which assumes every path segment is a plain
   * FormGroup and cannot walk through a FormArray index). Used by callers that
   * resolve group/array-nested FormGroups themselves (e.g. FormWidgetComponent,
   * AddressHistoryComponent) via resolveArrayItemFormPath or manual addControl.
   */
  public trackFormValidity(path: string, form: FormGroup): void {
    this.setupFormValidityTracking(path, form);
  }

  public checkFormTypeAndAssignValuesFromState(formId: string) {
    const formGroup = this.getFormPart(formId) as FormGroup;
    const parts = formId.split('.');
    const rootFormId = parts[0];
    const actionForAssociatedForm = formsAssociatedWithState.get(rootFormId as FormID);
    if (actionForAssociatedForm) {
      actionForAssociatedForm(this.injector, formGroup);
    }
  }
  public clearFormFromId(id: string): void {
    const form = this.getFormPart(id);
    if (form) {
      const defaultValues = this.formDefaultValues.get(id);
      if (defaultValues) {
        form.reset(defaultValues);
      } else {
        form.reset();
      }
    }
  }
}

/**
 * Extracts form paths from widget configurations with optional type filtering
 * @param widgets List of widget configurations
 * @param formType Optional type to filter ('self' or 'page')
 * @returns Array of unique form paths
 */
export const extractFormPaths = (widgets: UiZoneWidgetConfig[], formType?: 'self' | 'page'): string[] => {
  const formPaths: string[] = [];

  const extractPaths = (widget: UiZoneWidgetConfig) => {
    // Check main widget's workflowFormConfig
    if (
      widget.attributes.workflowFormConfig?.formPath &&
      (!formType || widget.attributes.workflowFormConfig.type === formType)
    ) {
      formPaths.push(widget.attributes.workflowFormConfig.formPath);
    }

    // Recursively process child components
    if ('childComponents' in widget) {
      const childComponents = widget.childComponents;
      if (Array.isArray(childComponents)) {
        // Handle both single and nested arrays of child components
        childComponents.flat().forEach((child) => {
          extractPaths(child);
        });
      }
    }
  };

  // Process each widget in the input array
  widgets.forEach(extractPaths);

  // Remove duplicates and filter out empty strings
  return [...new Set(formPaths)].filter(Boolean);
};
